<script>
	import { max } from 'd3-array'
	import { px, percent } from '../utils'

	export let data
	export let key
	export let subkeys
	export let sublabels
	export let limit = 0
	export let sorting

	export let color = '#7b7b7b'
	export let subcolors = ['#555', '#777', '#999', '#BBB']
	export let formatFunctionFull = (d) => `${d}`
	export let formatFunctionShort = (d) => `${d}`

	$: vMax = max(data, (d) => d[key])

	export let widthMeta = 200

	const itemHeight = 35

	let subkey_set = null

	function set_subkey(sk) {
		if (subkey_set === sk) {
			subkey_set = null
		} else {
			subkey_set = sk || null
		}
	}

	export let paddingRight = 0
	export let firstInside = true
</script>

{#if subkeys}
	<div class="bar-legend" class:hovering={subkey_set}>
		{#each subkeys as sk, j}
			<div
				class="bar-legend-item"
				on:mouseover={() => set_subkey(sk)}
				on:mouseout={() => set_subkey(null)}
				on:focus={() => set_subkey(sk)}
				on:blur={() => set_subkey(null)}
				on:touchend={() => set_subkey(sk)}
				style:opacity={subkey_set === sk ? 1 : null}>
				<div class="bar-legend-item-symbol" style="background: {subcolors[j]}">
				</div>
				<div class="bar-legend-item-label">
					{sublabels[j] || sk}
				</div>
			</div>
		{/each}
	</div>
{/if}

<div
	class="bar-chart"
	style:height={px((limit || data.length) * itemHeight)}
	class:hovering={subkey_set}>
	{#each data as d (d.iso_a2)}
		{@const frmt =
			sorting.indexOf(d.iso_a2) === 0
				? formatFunctionFull
				: formatFunctionShort}
		{@const p = (d[key] / vMax) * 100}

		<div
			class="item"
			style:top={px(sorting.indexOf(d.iso_a2) * itemHeight)}
			style:width="calc(100% - {paddingRight}px)">
			<div class="item-meta" style="flex-basis: {px(widthMeta)}">
				<div
					class="item-icon"
					style="background-image: url({PATH +
						'assets/flags/' +
						d['iso_a2'].toLowerCase() +
						'.svg'})">
				</div>
				<div class="item-name">
					{d['name']}
					{#if d['note']}
						<span class="item-note">{d['note']}</span>
					{/if}
				</div>
			</div>
			<div class="item-data">
				<div class="bar-wrapper" style:width={percent(p)}>
					<div class="bar" style:background={subkeys ? null : color}>
						{#each subkeys as sk, j}
							{#if d[sk] > 0}
								<div
									class="subbar"
									style="flex-basis: {percent(
										(d[sk] / d[key]) * 100
									)}; background: {subcolors[j]}"
									on:mouseover={() => set_subkey(sk)}
									on:mouseout={() => set_subkey(null)}
									on:focus={() => set_subkey(sk)}
									on:blur={() => set_subkey(null)}
									on:touchend={() => set_subkey(sk)}
									style:opacity={subkey_set === sk ? 1 : null}>
									<div
										class="value subvalue"
										class:inside={firstInside &&
											sorting.indexOf(d.iso_a2) === 0 &&
											(j == subkeys.length - 1 || d[sk] === d[key])}
										style:display={sk === subkey_set ? 'block' : 'none'}>
										{frmt(d[sk])}
									</div>
								</div>
							{/if}
						{/each}
					</div>
					<div
						class="value"
						class:inside={sorting.indexOf(d.iso_a2) === 0 && firstInside}>
						{frmt(d[key])}
					</div>
				</div>
			</div>
		</div>
	{/each}
</div>

<style lang="scss">
	.bar-chart {
		position: relative;
		overflow: hidden;
		transition: height 0.5s ease;
	}

	.item {
		font-size: 16px;
		line-height: 16px;
		display: flex;
		position: absolute;
		width: 100%;
		transition: top 0.3s ease;
	}

	.item-meta {
		display: flex;
		align-items: center;
		flex-grow: 0;
		flex-shrink: 0;
	}

	.item-icon {
		width: 16px;
		height: 16px;
		background-size: 24px;
		border: 1px solid #9b9b9b;
		background-position: center center;
		flex: 0 0 auto;
		margin-right: 5px;
		border-radius: 50%;
	}

	.item-name {
		font-weight: 500;
		flex: 1 0 auto;
	}

	.item-note {
		font-size: 13px;
		font-weight: normal;
	}

	.item-data {
		flex: 1 0 1px;
		position: relative;
		user-select: none;
	}

	.bar-wrapper {
		height: 20px;
		position: relative;
		min-width: 1px;
		transition: width 0.3s ease;
	}

	.bar {
		height: 100%;
		width: 100%;
		display: flex;
		user-select: none;
	}

	.subbar {
		flex-grow: 1;
		flex-shrink: 1;
		height: 100%;
		position: relative;
		box-shadow: -1px 0 0.1px 0 rgba(255, 255, 255, 0.4);
		transition:
			background 0.3s ease,
			opacity 0.3s ease;
	}

	.hovering .subbar {
		opacity: 0.2;
	}

	.value {
		font-size: 14px;
		line-height: 14px;
		position: absolute;
		left: calc(100% + 5px);
		top: 50%;
		transform: translateY(-50%);
		white-space: nowrap;
		color: #000;
		user-select: none;
		// pointer-events: none;
		padding-top: 2px;

		&.inside {
			left: auto;
			right: 5px;
			user-select: none;
			pointer-events: none;
			color: #fff;
		}
	}

	.hovering .value {
		display: none;
	}

	.bar-legend {
		font-size: 16px;
		line-height: 16px;
		margin-bottom: 35px;
		user-select: none;
		cursor: default;
		display: flex;
		justify-content: flex-start;
	}
	.bar-legend-item {
		display: inline-block;
		white-space: nowrap;
		padding-right: 10px;
	}
	.bar-legend-item-symbol {
		display: inline-block;
		white-space: nowrap;
		margin-right: 1px;
		width: 16px;
		height: 16px;
		vertical-align: middle;
	}
	.bar-legend-item-label {
		display: inline-block;
		white-space: nowrap;
		vertical-align: middle;
	}

	.hovering .bar-legend-item {
		opacity: 0.2;
	}
</style>
